import React from "react";
import { Stack } from "@mui/material";
import { useFormikContext } from "formik";

import SectionCard from "../../../shared/SectionCard";
import AnalyticsPreview from "../l-model/AnalyticsPreview";
import L1DimensionSelector from "../l-model/L1DimensionSelector";
import L2MethodSelector from "../l-model/L2MethodSelector";
import L3ContextSelector from "../l-model/L3ContextSelector";
import TeachingProcedureSection from "../l-model/TeachingProcedure";
import type { TargetFormValues } from "../TargetBuilderPanel.types";

interface TargetFormSection2Props {
  /** Parent goal's behavior subject — passed down to the prompt-hierarchy hide rule. */
  behaviorSubject?: "client" | "caregiver";
  replacementBehaviorTargetOptions?: Array<{ id: number; title: string }>;
}

/**
 * Section 2 — Measurement & instruction design.
 * Renders the L-Model cascade (L1 → L2 → L3), the teaching-procedure block
 * (Section 2.5) which unlocks once L3 is selected, and the L4 analytics
 * preview (derived metrics — visible once the full L-Model path is set).
 */
const TargetFormSection2: React.FC<TargetFormSection2Props> = ({
  behaviorSubject,
  replacementBehaviorTargetOptions,
}) => {
  const { values } = useFormikContext<TargetFormValues>();
  const showAnalytics = Boolean(values.l3_context);

  return (
    <SectionCard
      id="target-section-measurement"
      title="Section 2 — Measurement & instruction design"
      description="Behavioral dimension, recording method, instructional context, and analytics"
      withInnerCard={false}
    >
      <Stack gap={3}>
        <L1DimensionSelector />
        <L2MethodSelector />
        <L3ContextSelector />
        <TeachingProcedureSection
          behaviorSubject={behaviorSubject}
          replacementBehaviorTargetOptions={replacementBehaviorTargetOptions}
        />
        {showAnalytics && <AnalyticsPreview behaviorSubject={behaviorSubject} />}
      </Stack>
    </SectionCard>
  );
};

export default TargetFormSection2;
